import { Translate } from '@teliaee/telia-translate';
import { action, observable, runInAction } from 'mobx';
import { ISelectOption, TSelectValue, ISelectObjectProps } from 'telia-front-react';
import { JobListProps, JobListResultProps } from '../content/JobList';
import ErrorUtil from '../error/ErrorUtil';
import API from '../util/ApiUtil';

const THIRTY_SECONDS = 30 * 1000;

export const JOB_OFFERS = 'JOB_OFFERS';
export const INTERNSHIPS = 'INTERNSHIPS';
export const DEFAULT_OFFERS_PER_PAGE = 30;

export interface JobResultItem extends JobListResultProps {
  positionType: string;
  description: string;
  countries: string[];
  cities: string[];
  locationsMap: Record<string, string[]>;
}

export interface JobListItem {
  listProps?: JobListProps;
  isLoading?: boolean;
  isErroneous?: boolean;
  isEmpty?: boolean;
  showPaginator: boolean;
  currentPage: number;
  totalPages: number;
}

class JobOffersStore {
  @observable accessor jobOffersListMap: Map<string, JobListItem> = new Map<string, JobListItem>();
  @observable accessor perPage: number;
  public allJobOffersMap: Map<string, JobResultItem[]> = new Map<string, JobResultItem[]>().set(JOB_OFFERS, []).set(INTERNSHIPS, []);
  private filteredJobOffersMap: Map<string, JobResultItem[]> = new Map<string, JobResultItem[]>().set(JOB_OFFERS, []).set(INTERNSHIPS, []);
  private positionTypes: string[] = [];
  private countries: string[] = [];
  private cities: string[] = [];
  private locationsMap: Record<string, string[]> = {};
  private readonly search: (offerType: string) => void;

  constructor() {
    Array.of(INTERNSHIPS, JOB_OFFERS).forEach((offerType: string) => this.initJobOffersListMap(offerType));
    this.search = this.filterJobOffers;
  }

  public initJobOffers = async (offerType: string, translate: Translate, offersPerPage?: number) => {
    this.perPage = offersPerPage ? offersPerPage : DEFAULT_OFFERS_PER_PAGE;
    await this.fetchSearchFieldValues(offerType);
    this.initJobOffersList(offerType, translate);
    await this.fetchJobOffers(offerType, translate);
  };

  public isInternships = (offerType: string): boolean => {
    return offerType === INTERNSHIPS;
  };

  public changePage = (page: number, offerType: string) => {
    runInAction(() => {
      this.jobOffersListMap.get(offerType).currentPage = page;
      this.paginateOffers(offerType);
    });
  };

  public setSearchTerm = (term: string, offerType: string) => {
    runInAction(() => {
      if (this.jobOffersListMap.get(offerType).listProps.search.value !== term) {
        this.jobOffersListMap.get(offerType).listProps.search.value = term;
        this.search(offerType);
      }
    });
  };

  public setSelectedCountry = (newValue: TSelectValue, offerType: string, translate: Translate) => {
    runInAction(() => {
      const keyword = newValue as ISelectOption;
      this.jobOffersListMap.get(offerType).listProps.citySelect.value = { label: 'all', value: 'all' };
      this.jobOffersListMap.get(offerType).listProps.countrySelect.value = {
        label: keyword.label,
        value: keyword.value
      };
      this.filterJobOffers(offerType);
      this.filterCitySelectOptions(offerType, translate);
    });
  };

  public setSelectedCity = (newValue: TSelectValue, offerType: string) => {
    runInAction(() => {
      const keyword = newValue as ISelectOption;
      this.jobOffersListMap.get(offerType).listProps.citySelect.value = {
        label: keyword.label,
        value: keyword.value
      };
      this.filterJobOffers(offerType);
    });
  };

  public setSelectedPosition = (newValue: TSelectValue, offerType: string) => {
    runInAction(() => {
      const keyword = newValue as ISelectOption;
      this.jobOffersListMap.get(offerType).listProps.positionSelect.value = {
        label: keyword.label,
        value: keyword.value
      };
      this.filterJobOffers(offerType);
    });
  };

  @action
  private initJobOffersListMap(offerType: string) {
    this.jobOffersListMap.set(offerType, {
      listProps: {} as JobListProps,
      currentPage: 1,
      showPaginator: false,
      isEmpty: false,
      isLoading: true,
      isErroneous: false,
      totalPages: 1
    });
  }

  private filterJobOffers = (offerType: string) => {
    runInAction(() => {
      this.filteredJobOffersMap.set(
        offerType,
        this.allJobOffersMap.get(offerType).filter((a) => this.filterPredicate(a, offerType))
      );
      if (!this.filteredJobOffersMap.get(offerType).length) {
        this.jobOffersListMap.get(offerType).isEmpty = true;
        this.jobOffersListMap.get(offerType).showPaginator = false;
        this.paginateOffers(offerType);
      } else {
        this.jobOffersListMap.get(offerType).totalPages = Math.ceil(this.filteredJobOffersMap.get(offerType).length / this.perPage);
        this.jobOffersListMap.get(offerType).currentPage = 1;
        this.jobOffersListMap.get(offerType).showPaginator = this.jobOffersListMap.get(offerType).totalPages > 1;
        this.jobOffersListMap.get(offerType).isEmpty = false;
        this.paginateOffers(offerType);
      }
    });
  };

  private filterCitySelectOptions = (offerType: string, translate: Translate) => {
    runInAction(() => {
      const countryValue = this.isInternships(offerType)
        ? 'Estonia'
        : (this.jobOffersListMap.get(offerType).listProps.countrySelect.value as ISelectOption).value;
      this.cities = [];
      (countryValue !== 'all' ? this.filteredJobOffersMap.get(offerType) : this.allJobOffersMap.get(offerType)).forEach((job) => {
        job.cities.forEach((city) => {
          if (this.cities.indexOf(city) === -1) {
            this.cities.push(city);
          }
        });
      });
      this.jobOffersListMap.get(offerType).listProps.citySelect = this.getCitySelectOptions(offerType, translate);
    });
  };

  private filterPredicate = (item: JobResultItem, offerType: string) => {
    const countrySelect = this.isInternships(offerType)
      ? 'Estonia'
      : (this.jobOffersListMap.get(offerType).listProps.countrySelect.value as ISelectOption).value;
    const citySelect = (this.jobOffersListMap.get(offerType).listProps.citySelect.value as ISelectOption).value;
    const positionSelect = (this.jobOffersListMap.get(offerType).listProps.positionSelect.value as ISelectOption).value;
    const textFilter = this.jobOffersListMap.get(offerType).listProps.search.value;

    return (
      (countrySelect === 'all' || item.countries.includes(countrySelect)) &&
      (citySelect === 'all' || item.cities.includes(citySelect)) &&
      item.positionType === (positionSelect !== 'all' ? positionSelect : item.positionType) &&
      (textFilter ? item.description.toLowerCase().includes(textFilter.toLowerCase()) || item.name.toLowerCase().includes(textFilter.toLowerCase()) : true)
    );
  };

  private static formatDate = (date: string) => {
    return date ? date.slice(0, 10) : '';
  };

  fetchJobOffers = async (offerType: string, translate: Translate) => {
    runInAction(() => {
      this.jobOffersListMap.get(offerType).isLoading = true;
      this.jobOffersListMap.get(offerType).showPaginator = true;
    });
    return API.get(`/workday/job-openings?internships=${this.isInternships(offerType)}`, { timeout: THIRTY_SECONDS })
      .then((response) => {
        this.addJobOffersToMap(response.data.openings, offerType);
      })
      .then(() => runInAction(() => (this.jobOffersListMap.get(offerType).isLoading = false)))
      .then(() => this.filterCitySelectOptions(offerType, translate))
      .catch((e) => {
        ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
        runInAction(() => {
          this.jobOffersListMap.get(offerType).isErroneous = true;
          this.jobOffersListMap.get(offerType).isLoading = false;
        });
      });
  };

  fetchSearchFieldValues = async (offerType: string) => {
    return API.get(`/workday/job-openings/search-field-values?internships=${this.isInternships(offerType)}`, {
      timeout: THIRTY_SECONDS
    })
      .then((response) => {
        runInAction(() => {
          this.countries = response.data.countries;
          this.cities = response.data.cities;
          this.locationsMap = response.data.locationsMap;
          this.positionTypes = response.data.positionTypes;
        });
      })
      .catch((e) => {
        ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      });
  };

  @action
  initJobOffersList(offerType: string, translate: Translate) {
    this.jobOffersListMap.get(offerType).listProps = {
      results: [],
      resultHeaders: [
        this.isInternships(offerType) ? translate('workday.head.internshipOfferName') : translate('workday.head.jobOfferName'),
        translate('workday.head.country'),
        translate('workday.head.city'),
        translate('workday.head.deadline')
      ],
      careersLink: translate('workday.careersLink'),
      careersNote: translate('workday.careersNote'),
      careersText: translate('workday.careersText'),
      countrySelect: !this.isInternships(offerType) && this.getCountrySelectOptions(offerType, translate),
      citySelect: this.getCitySelectOptions(offerType, translate),
      positionSelect: this.getPositionSelectOptions(offerType, translate),
      search: {
        id: 'job-offer-search',
        label: 'search',
        placeholder: this.jobOffersListMap.get(offerType)?.listProps?.search?.value
          ? this.jobOffersListMap.get(offerType).listProps.search.value
          : translate('workday.search'),
        value: this.jobOffersListMap.get(offerType)?.listProps.search?.value ? this.jobOffersListMap.get(offerType).listProps.search.value : ''
      }
    };
  }

  @action
  addJobOffersToMap(offers: JobResultItem[], offerType: string) {
    offers.forEach((offer) => {
      offer.date = JobOffersStore.formatDate(offer.date);
      offer.country = offer.countries.join(', ');
      offer.city = offer.cities.join(', ');
    });
    this.allJobOffersMap.set(offerType, offers);
    this.filterJobOffers(offerType);
    this.paginateOffers(offerType);
  }

  private paginateOffers = (offerType: string) => {
    this.jobOffersListMap.get(offerType).listProps.results = this.filteredJobOffersMap
      .get(offerType)
      .slice((this.jobOffersListMap.get(offerType).currentPage - 1) * this.perPage, this.jobOffersListMap.get(offerType).currentPage * this.perPage);
  };

  private getCountrySelectOptions = (offerType: string, translate: Translate): ISelectObjectProps => {
    const selectOptions: ISelectOption[] = [];
    selectOptions.push({
      label: translate('workday.allOptions'),
      value: 'all'
    });
    this.countries.forEach((country) => {
      selectOptions.push({ label: country, value: country });
    });

    return {
      id: 'country-select',
      label: translate('workday.head.country'),
      value: (this.jobOffersListMap.get(offerType)?.listProps?.countrySelect?.value as ISelectOption)?.value
        ? {
            value: (this.jobOffersListMap.get(offerType).listProps.countrySelect.value as ISelectOption).value,
            label: (this.jobOffersListMap.get(offerType).listProps.countrySelect.value as ISelectOption).value
          }
        : {
            value: 'Estonia',
            label: 'Estonia'
          },
      options: selectOptions
    };
  };

  private getPositionSelectOptions = (offerType: string, translate: Translate): ISelectObjectProps => {
    const selectOptions: ISelectOption[] = [];
    selectOptions.push({
      label: translate('workday.allOptions'),
      value: 'all'
    });
    this.positionTypes.forEach((positionType) => {
      selectOptions.push({ label: positionType, value: positionType });
    });

    return {
      id: 'position-type-select',
      label: translate('workday.positionType'),
      value: (this.jobOffersListMap.get(offerType)?.listProps?.positionSelect?.value as ISelectOption)?.value
        ? {
            value: (this.jobOffersListMap.get(offerType).listProps.positionSelect.value as ISelectOption).value,
            label: (this.jobOffersListMap.get(offerType).listProps.positionSelect.value as ISelectOption).value
          }
        : { value: 'all', label: translate('workday.allOptions') },
      options: selectOptions
    };
  };

  public getCitySelectOptions = (offerType: string, translate: Translate): ISelectObjectProps => {
    const citySelectOptions = {
      id: 'city-select',
      label: translate('workday.head.city'),
      value: (this.jobOffersListMap.get(offerType)?.listProps?.citySelect?.value as ISelectOption)?.value
        ? {
            value: (this.jobOffersListMap.get(offerType).listProps.citySelect.value as ISelectOption).value,
            label: (this.jobOffersListMap.get(offerType).listProps.citySelect.value as ISelectOption).value
          }
        : { label: translate('workday.allOptions'), value: 'all' },
      options: [{ value: 'all', label: translate('workday.allOptions') }]
    };
    let citiesToAdd;
    if ((this.jobOffersListMap.get(offerType)?.listProps?.countrySelect?.value as ISelectOption)?.value) {
      const country = (this.jobOffersListMap.get(offerType).listProps.countrySelect.value as ISelectOption).value;
      if (country !== 'all') {
        citiesToAdd = this.locationsMap[country];
      } else {
        citiesToAdd = this.cities;
      }
    } else {
      citiesToAdd = this.cities;
    }
    citiesToAdd?.forEach((city) => {
      citySelectOptions.options.push({ value: city, label: city });
    });
    return citySelectOptions;
  };
}

export default JobOffersStore;
